import { cn } from "@/lib/cn";
import { useTheme } from "@/hooks/useTheme";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { SlideMode } from "@/lib/constants";

const DESIGN_W = 1024;
const DESIGN_H = 576;

export function Slide({
  children,
  isActive,
  index,
  title,
  totalSlides,
  badge,
  mode = "presentation",
}: {
  children: ReactNode;
  isActive: boolean;
  index: number;
  title: string;
  totalSlides: number;
  badge?: string;
  mode?: SlideMode;
}) {
  const { theme } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [actualW, setActualW] = useState(0);
  const [actualH, setActualH] = useState(0);
  const isExport = mode === "export";

  useEffect(() => {
    if (isExport) return; // Fixed scale=1 in export mode
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) {
        const w = entry.contentRect.width;
        const s = w / DESIGN_W;
        setScale(s);
        setActualW(Math.round(w));
        setActualH(Math.round(w * (DESIGN_H / DESIGN_W)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isExport]);

  const isDark = theme === "dark";
  const nodeColor = isDark ? "#60a5fa" : "#3b82f6";
  const artboardBg = isDark ? "#121212" : "#ffffff";
  const strokeLight = isDark ? "#1f1f1f" : "#e4e4e7";
  const strokeColor = isDark ? "#3a3a3a" : "#a1a1aa";
  const num = String(index + 1).padStart(2, "0");
  const total = String(totalSlides).padStart(2, "0");

  return (
    <div className={cn(
      "w-full flex items-center justify-center",
      mode === "presentation" ? "h-screen px-4 pb-12" : ""
    )}>
      <div
        className={cn(
          mode === "presentation" ? "w-[min(98%,calc(88vh*16/9))]" : isExport ? "" : "w-full max-w-[1440px]",
          mode === "presentation"
            ? cn("transition-all duration-700", isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")
            : "opacity-100"
        )}
        style={isExport ? { width: DESIGN_W } : undefined}
      >
        {/* Artboard metadata — hidden in export mode */}
        {!isExport && (
          <div className="flex justify-between mb-2 font-[family-name:var(--font-mono)] text-[10px] uppercase tracking-wider text-[var(--color-fg-tertiary)]">
            <span>Artboard {index + 1} : {title}</span>
            <span>W: {actualW} H: {actualH}</span>
          </div>
        )}

        {/* Artboard frame — maintains 16:9 via aspect-ratio, scales content inside */}
        <div
          ref={wrapperRef}
          className={isExport ? "relative" : "relative rounded"}
          style={{
            ...(isExport
              ? { width: DESIGN_W, height: DESIGN_H }
              : { aspectRatio: "16 / 9", border: `1px solid ${nodeColor}` }),
            background: artboardBg,
            overflow: "hidden",
          }}
        >
          {/* 8 bounding-box nodes — hidden in export mode */}
          {!isExport && [
            { top: -3, left: -3 },
            { top: -3, left: "calc(50% - 2.5px)" },
            { top: -3, right: -3 },
            { top: "calc(50% - 2.5px)", right: -3 },
            { bottom: -3, right: -3 },
            { bottom: -3, left: "calc(50% - 2.5px)" },
            { bottom: -3, left: -3 },
            { top: "calc(50% - 2.5px)", left: -3 },
          ].map((pos, i) => (
            <div
              key={i}
              className="absolute w-[5px] h-[5px] z-10"
              style={{ ...pos, background: nodeColor } as React.CSSProperties}
            />
          ))}

          {/* Scaled content layer — fixed design size, scaled down to fit */}
          <div
            className="relative flex flex-col"
            style={{
              width: DESIGN_W,
              height: DESIGN_H,
              transform: isExport ? "scale(1)" : `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            {/* Internal grid background */}
            {isExport ? (
              /* Export: SVG with explicit lines — immune to Chromium PDF backgroundSize bug */
              <svg
                className="absolute inset-0 pointer-events-none opacity-30 z-0"
                viewBox={`0 0 ${DESIGN_W} ${DESIGN_H}`}
                width={DESIGN_W}
                height={DESIGN_H}
                style={{ position: "absolute", top: 0, left: 0 }}
              >
                <g stroke={strokeLight} strokeWidth="1">
                  {Array.from({ length: Math.floor(DESIGN_W / 40) + 1 }, (_, i) => (
                    <line key={`v${i}`} x1={i * 40} y1={0} x2={i * 40} y2={DESIGN_H} />
                  ))}
                  {Array.from({ length: Math.floor(DESIGN_H / 40) + 1 }, (_, i) => (
                    <line key={`h${i}`} x1={0} y1={i * 40} x2={DESIGN_W} y2={i * 40} />
                  ))}
                </g>
              </svg>
            ) : (
              /* Normal/presentation: CSS gradient — compact and performant on screen */
              <div
                className="absolute inset-0 pointer-events-none opacity-30 z-0"
                style={{
                  backgroundImage: `linear-gradient(${strokeLight} 1px, transparent 1px), linear-gradient(90deg, ${strokeLight} 1px, transparent 1px)`,
                  backgroundSize: "40px 40px",
                }}
              />
            )}

            {/* Slide header */}
            <div className="relative z-[1] flex justify-between items-center px-[40px] pt-[32px]">
              {badge ? (
                <span className="inline-block px-[12px] py-[4px] text-[10px] font-medium tracking-wider uppercase border border-[var(--color-border)] rounded-full text-[var(--color-fg-secondary)] font-[family-name:var(--font-mono)]">
                  {badge}
                </span>
              ) : (
                <img src={isDark ? "/cosx-logo-dark-1.svg" : "/cosx-logo.svg"} alt="COSX" className="h-[44px] w-auto" />
              )}
              <div className="flex items-center gap-4 font-[family-name:var(--font-mono)]" style={{ color: strokeColor }}>
                {!badge && (
                  <span className="text-[10px] uppercase tracking-widest text-[var(--color-fg-tertiary)]">
                    Confidential – For authorised use only
                  </span>
                )}
                <span className="text-[12px]">{num} / {total}</span>
              </div>
            </div>

            {/* Slide content */}
            <div className={cn(
              "relative z-[1] px-[40px] pb-[32px] flex-1 flex flex-col",
              badge ? "pt-[12px] justify-start" : "pt-[8px] justify-center"
            )}>
              {children}
            </div>

            {/* Confidential footer — content slides only */}
            {badge && (
              <div className="absolute bottom-[16px] right-[40px] z-[1] text-right">
                <p className="text-[8px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-widest leading-relaxed">
                  This document is confidential and proprietary to COSX. It is intended solely for authorised recipients.
                  <br />
                  Unauthorised copying, distribution, or disclosure is strictly prohibited and may be unlawful.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
